import React, { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { Reveal } from "../motion/Reveal";
import { SectionKicker } from "./Manifesto";
import { api } from "../../lib/api";

export const Testimonials = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const next = useCallback(() => setIdx((i) => (items.length ? (i + 1) % items.length : 0)), [items.length]);
  const prev = () => setIdx((i) => (items.length ? (i - 1 + items.length) % items.length : 0));

  useEffect(() => {
    if (!items.length) return;
    const id = setInterval(next, 6000);
    return () => clearInterval(id);
  }, [items.length, next]);

  if (!items.length) return null;
  const cur = items[idx];

  return (
    <section className="relative z-10 py-24 md:py-32 border-t border-white/10 bg-[#161616]" data-testid="testimonials-section">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <Reveal>
          <SectionKicker>{t.testimonials.kicker}</SectionKicker>
          <h2 className="font-display font-extrabold tracking-tighter text-4xl md:text-6xl leading-[0.95]">
            {t.testimonials.title}
          </h2>
        </Reveal>

        <div className="mt-14 relative min-h-[280px]">
          <Quote size={80} className="absolute -top-6 -left-2 text-emerald2/15" />
          <AnimatePresence mode="wait">
            <motion.div
              key={cur.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              data-testid="testimonial-card"
            >
              <p className="font-display text-2xl md:text-4xl font-medium leading-snug tracking-tight max-w-4xl">
                "{cur.quote}"
              </p>
              <div className="mt-8 flex items-center gap-4">
                <img src={cur.photo} alt={cur.name} className="w-14 h-14 rounded-full object-cover border border-white/20" />
                <div>
                  <p className="font-semibold">{cur.name}</p>
                  <p className="text-sm text-white/50">{cur.role}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center gap-4">
          <button onClick={prev} data-testid="testimonial-prev" className="grid place-items-center w-11 h-11 rounded-full border border-white/20 hover:border-lime2 hover:text-lime2 transition-colors">
            <ArrowLeft size={16} />
          </button>
          <button onClick={next} data-testid="testimonial-next" className="grid place-items-center w-11 h-11 rounded-full border border-white/20 hover:border-lime2 hover:text-lime2 transition-colors">
            <ArrowRight size={16} />
          </button>
          <div className="flex gap-1.5 ml-2">
            {items.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-7 bg-lime2" : "w-1.5 bg-white/20"}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
